import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Logo } from '../../components/branding/Logo';
import { scrollToSection } from './shared';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '../../components/overlays/Popover';
import {
  SUPPORTED_HOMEPAGE_LOCALES,
  LOCALE_FLAGS,
  LOCALE_LABELS,
  type HomepageStrings,
} from '../../i18n/homepage';

const EN_DEFAULTS = {
  nav_features: 'Features',
  nav_themes: 'Themes',
  nav_how_it_works: 'How it Works',
  nav_pricing: 'Pricing',
  nav_faq: 'FAQ',
  nav_login: 'Log in',
  nav_cta: 'Create Invite',
  nav_app: 'Go to App',
} as const;

type NavStrings = Pick<
  HomepageStrings,
  | 'nav_features'
  | 'nav_themes'
  | 'nav_how_it_works'
  | 'nav_pricing'
  | 'nav_faq'
  | 'nav_login'
  | 'nav_cta'
  | 'nav_app'
>;

const ALL_LOCALES = ['en', ...SUPPORTED_HOMEPAGE_LOCALES] as const;

function LanguagePicker({ onSelect }: { onSelect?: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active locale from current path
  const pathLocale = location.pathname.slice(1) as string;
  const activeLocale = (SUPPORTED_HOMEPAGE_LOCALES as readonly string[]).includes(pathLocale)
    ? pathLocale
    : 'en';

  function handleLocale(locale: string) {
    onSelect?.();
    sessionStorage.setItem('locale-redirect', locale);
    navigate(locale === 'en' ? '/' : `/${locale}`);
  }

  return (
    <Popover>
      <PopoverTrigger className="flex items-center gap-1 p-2 text-text-secondary hover:text-text-primary transition-colors rounded-lg hover:bg-background-elevated">
        <Globe className="w-4 h-4" />
        <span className="hidden lg:inline leading-none" style={{ fontSize: '18px', lineHeight: 1 }}>{LOCALE_FLAGS[activeLocale]}</span>
      </PopoverTrigger>
      <PopoverContent align="right" className="w-44 p-1 bg-background border border-border rounded-xl shadow-lg">
        {ALL_LOCALES.map((locale) => (
          <button
            key={locale}
            type="button"
            onClick={() => handleLocale(locale)}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
              activeLocale === locale
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-text-primary hover:bg-background-elevated'
            }`}
          >
            <span>{LOCALE_FLAGS[locale]}</span>
            <span>{LOCALE_LABELS[locale]}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function Navigation({ strings, onMenuOpenChange }: { strings?: NavStrings; onMenuOpenChange?: (open: boolean) => void }) {
  const s = strings ?? EN_DEFAULTS;
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = (open: boolean) => {
    setIsOpen(open);
    onMenuOpenChange?.(open);
  };
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  useEffect(() => { setMounted(true); }, []);

  const pathLocale = location.pathname.slice(1);
  const activeLocale = (SUPPORTED_HOMEPAGE_LOCALES as readonly string[]).includes(pathLocale) ? pathLocale : 'en';
  const localePath = activeLocale === 'en' ? '/' : `/${activeLocale}`;

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    if (location.pathname === localePath) {
      scrollToSection(e, id);
    } else {
      navigate(`${localePath}#${id}`);
    }
  };

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to={localePath} className="flex items-center">
            <Logo className="h-10" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            <a href="#features" onClick={(e) => handleNavClick(e, 'features')} className="text-text-secondary hover:text-text-primary transition-colors text-sm lg:text-base">{s.nav_features}</a>
            <a href="#theme-demos" onClick={(e) => handleNavClick(e, 'theme-demos')} className="text-text-secondary hover:text-text-primary transition-colors text-sm lg:text-base">{s.nav_themes}</a>
            <a href="#how-it-works" onClick={(e) => handleNavClick(e, 'how-it-works')} className="text-text-secondary hover:text-text-primary transition-colors text-sm lg:text-base">{s.nav_how_it_works}</a>
            <a href="#pricing" onClick={(e) => handleNavClick(e, 'pricing')} className="text-text-secondary hover:text-text-primary transition-colors text-sm lg:text-base">{s.nav_pricing}</a>
            <a href="#faq" onClick={(e) => handleNavClick(e, 'faq')} className="text-text-secondary hover:text-text-primary transition-colors text-sm lg:text-base">{s.nav_faq}</a>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3 lg:gap-4">
            <LanguagePicker />
            {!(mounted && isAuthenticated) && (
              <Link
                to="/login"
                className="text-text-primary font-medium hover:text-primary transition-colors text-sm lg:text-base"
              >
                {s.nav_login}
              </Link>
            )}
            <Link
              to={mounted && isAuthenticated ? "/app/home" : "/create"}
              className="btn btn-primary"
            >
              {mounted && isAuthenticated ? s.nav_app : s.nav_cta}
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => toggleMenu(!isOpen)}
            className="md:hidden p-2 text-text-primary"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu - Full screen overlay */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="fixed inset-x-0 top-16 bottom-0 z-50 md:hidden"
              style={{
                backgroundColor: '#f4f1eb',
                width: '100%',
                height: 'calc(100vh - 4rem)'
              }}
            >
              <div className="flex flex-col h-full px-8 py-10">
                {/* Navigation Links */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 20, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="flex flex-col gap-6 flex-1"
                >
                  <a
                    href="#features"
                    onClick={(e) => { handleNavClick(e, 'features'); toggleMenu(false); }}
                    className="text-2xl font-medium text-text-primary hover:text-primary transition-colors py-2"
                  >
                    {s.nav_features}
                  </a>
                  <a
                    href="#theme-demos"
                    onClick={(e) => { handleNavClick(e, 'theme-demos'); toggleMenu(false); }}
                    className="text-2xl font-medium text-text-primary hover:text-primary transition-colors py-2"
                  >
                    {s.nav_themes}
                  </a>
                  <a
                    href="#how-it-works"
                    onClick={(e) => { handleNavClick(e, 'how-it-works'); toggleMenu(false); }}
                    className="text-2xl font-medium text-text-primary hover:text-primary transition-colors py-2"
                  >
                    {s.nav_how_it_works}
                  </a>
                  <a
                    href="#pricing"
                    onClick={(e) => { handleNavClick(e, 'pricing'); toggleMenu(false); }}
                    className="text-2xl font-medium text-text-primary hover:text-primary transition-colors py-2"
                  >
                    {s.nav_pricing}
                  </a>
                  <a
                    href="#faq"
                    onClick={(e) => { handleNavClick(e, 'faq'); toggleMenu(false); }}
                    className="text-2xl font-medium text-text-primary hover:text-primary transition-colors py-2"
                  >
                    {s.nav_faq}
                  </a>

                  {/* Language picker in mobile menu */}
                  <Popover>
                    <PopoverTrigger className="flex items-center gap-3 text-2xl font-medium text-text-primary hover:text-primary transition-colors py-2">
                      <Globe className="w-6 h-6" />
                      <span>{LOCALE_FLAGS[activeLocale]} {LOCALE_LABELS[activeLocale]}</span>
                    </PopoverTrigger>
                    <PopoverContent align="left" side="top" animation="fadeSlideUp" className="w-52 p-1 bg-background border border-border rounded-xl shadow-lg">
                      {ALL_LOCALES.map((locale) => (
                        <button
                          key={locale}
                          type="button"
                          onClick={() => {
                            toggleMenu(false);
                            sessionStorage.setItem('locale-redirect', locale);
                            navigate(locale === 'en' ? '/' : `/${locale}`);
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                            activeLocale === locale
                              ? 'bg-primary/10 text-primary font-medium'
                              : 'text-text-primary hover:bg-background-elevated'
                          }`}
                        >
                          <span>{LOCALE_FLAGS[locale]}</span>
                          <span>{LOCALE_LABELS[locale]}</span>
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                </motion.div>

                {/* CTA Buttons at Bottom */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 20, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="flex flex-col gap-3 pb-safe"
                >
                  {!(mounted && isAuthenticated) && (
                    <Link
                      to="/login"
                      className="text-center py-3 text-lg font-medium text-text-primary hover:text-primary transition-colors"
                    >
                      {s.nav_login}
                    </Link>
                  )}
                  <Link
                    to={mounted && isAuthenticated ? "/app/home" : "/create"}
                    className="btn-hero btn-primary text-center"
                  >
                    {mounted && isAuthenticated ? s.nav_app : s.nav_cta}
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}
